<template>
  <div 
    class="multi_select_input" 
    :class="{ 'scrollable-mode': scrollable, 'drag-over': isDragOver }"
    @dragover="handleDragOver"
    @dragenter="handleDragEnter"
    @dragleave="handleDragLeave"
    @drop.prevent="handleDrop"
  >
    <div
      class="content-container"
      :class="{ 'scrollable-content': scrollable }"
      ref="contentRef"
    >
      <span
        v-for="(item, index) in selectedItems"
        :key="index"
        class="selected_item_tag"
        :class="{ 'scrollable-item': scrollable, 'dragging': draggingIndex === index, 'draggable-tag': draggable }"
        :draggable="draggable"
        @dragstart="handleDragStart($event, item, index)"
        @dragend="handleDragEnd"
      >
        {{ item }}
        <ThemeImage
          @click="removeItem(index)"
          class="remove_icon"
          :icon="imageNames.CLOSE_GREY"
          alt="remove_icon"
        />
      </span>
      <input
        type="text"
        v-model="inputValue"
        @keydown="handleKeyDown"
        @focus="handleFocus"
        @blur="handleBlur"
        @input="handleInput"
        class="input_field"
        :class="{ 'scrollable-input': scrollable }"
        ref="inputRef"
      />
    </div>
    <transition name="dropdown">
      <div
        v-if="showDropdown && filteredItems.length > 0 && inputValue.length > 0"
        class="dropdown"
      >
        <div
          v-for="(item, index) in filteredItems"
          :key="index"
          @mousedown.prevent="toggleSelectItem(item)"
          @mouseover="highlightedIndex = index"
          :class="[
            'dropdown_item',
            { selected: selectedItems.includes(getItemValue(item)) },
            { highlighted: highlightedIndex === index },
          ]"
          ref="dropdownItems"
        >
          {{ formatItem(item) }}
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, watch, nextTick, onMounted } from "vue";

import { imageNames } from "@/src/constants/imageConstants.js";

const props = defineProps({
  modelValue: {
    type: Array,
    required: true,
  },
  options: {
    type: Array,
    required: false,
  },
  onInput: {
    type: Function,
    required: false,
  },
  formatItem: {
    type: Function,
    required: false,
  },
  valueField: {
    type: String,
    default: "email",
  },
  autofocus: {
    type: Boolean,
    default: false,
  },
  scrollable: {
    type: Boolean,
    default: false,
  },
  fieldId: {
    type: String,
    default: "",
  },
  draggable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "item-dropped"]);
const inputValue = ref("");
const selectedItems = ref([...props.modelValue]);
const showDropdown = ref(false);
const allItems = ref([...(props.options || []).filter(item => item)]);
const filteredItems = ref([...allItems.value]);
const highlightedIndex = ref(-1);
const inputRef = ref(null);
const contentRef = ref(null);
const draggingIndex = ref(-1);
const isDragOver = ref(false);

const scrollToRight = () => {
  if (props.scrollable && contentRef.value) {
    nextTick(() => {
      contentRef.value.scrollLeft = contentRef.value.scrollWidth;
    });
  }
};

const focusInput = () => {
  nextTick(() => {
    inputRef.value?.focus();
  });
};

watch(
  () => props.modelValue,
  (newValue) => {
    selectedItems.value = [...newValue];
    scrollToRight();
  },
);

watch(
  () => props.options,
  (newValue) => {
    allItems.value = [...(newValue || []).filter(item => item)];
    filterItems();
  },
);

watch(
  () => props.scrollable,
  () => {
    focusInput();
    scrollToRight();
  },
);

watch(highlightedIndex, (newIndex) => {
  if (newIndex !== -1) {
    nextTick(() => {
      const dropdownItems = document.querySelectorAll(".dropdown_item");
      if (dropdownItems[newIndex]) {
        dropdownItems[newIndex].scrollIntoView({
          block: "nearest",
        });
      }
    });
  }
});

const handleInput = () => {
  inputValue.value = inputValue.value.replace(/\s/g, "");
  props.onInput(inputValue.value);
  filterItems();
};

const addItem = () => {
  if (inputValue.value.trim() !== "") {
    const items = inputValue.value
      .split(",")
      .map((item) => item.trim())
      .filter((item) => item !== "");
    items.forEach((item) => {
      if (!selectedItems.value.includes(item)) {
        selectedItems.value.push(item);
      }
    });
    emit("update:modelValue", selectedItems.value);
    inputValue.value = "";
    setTimeout(() => {
      handleInput();
    }, 500);
    scrollToRight();
  }
};

const removeItem = (index) => {
  selectedItems.value.splice(index, 1);
  emit("update:modelValue", selectedItems.value);
  scrollToRight();
};

const handleFocus = () => {
  showDropdown.value = true;
  nextTick(() => {
    if (filteredItems.value.length > 0) {
      highlightedIndex.value = 0;
    }
  });
};

const handleBlur = () => {
  addItem();
  setTimeout(() => {
    showDropdown.value = false;
    highlightedIndex.value = -1;
  }, 200);
};

const getItemValue = (item) => {
  if (!item) return '';
  return item[props.valueField];
};

const filterItems = () => {
  if (inputValue.value) {
    const query = inputValue.value.toLowerCase();
    filteredItems.value = allItems.value.filter((item) =>
      props?.formatItem(item)?.trim().toLowerCase().includes(query),
    );
  } else {
    filteredItems.value = [...allItems.value];
  }
  highlightedIndex.value = filteredItems.value.length > 0 ? 0 : -1;
};

const toggleSelectItem = (item) => {
  const value = getItemValue(item);
  if (selectedItems.value.includes(value)) {
    selectedItems.value = selectedItems.value.filter(
      (selectedItem) => selectedItem !== value,
    );
  } else {
    selectedItems.value.push(value);
  }
  inputValue.value = "";
  handleInput();
  emit("update:modelValue", selectedItems.value);
  scrollToRight();
};

const handleKeyDown = (event) => {
  switch (event.key) {
    case "Backspace":
      if (inputValue.value === "" && selectedItems.value.length > 0) {
        removeItem(selectedItems.value.length - 1);
      }
      break;
    case "ArrowDown":
      event.preventDefault();
      if (highlightedIndex.value < filteredItems.value.length - 1) {
        highlightedIndex.value++;
      } else {
        highlightedIndex.value = 0;
      }
      break;
    case "ArrowUp":
      event.preventDefault();
      if (highlightedIndex.value > 0) {
        highlightedIndex.value--;
      } else {
        highlightedIndex.value = filteredItems.value.length - 1;
      }
      break;
    case "Enter":
      event.preventDefault();
      if (inputValue.value.trim() !== "" && highlightedIndex.value !== -1) {
        toggleSelectItem(filteredItems.value[highlightedIndex.value]);
      } else if (inputValue.value.trim() !== "") {
        addItem();
      }
      break;
    case " ":
      event.preventDefault();
      break;
  }
};

const handleDragStart = (event, item, index) => {
  if (!props.draggable) {
    event.preventDefault();
    return;
  }
  draggingIndex.value = index;
  event.dataTransfer.effectAllowed = 'move';
  event.dataTransfer.setData('application/x-supesales-recipient', JSON.stringify({
    item: item,
    sourceField: props.fieldId
  }));
};

const handleDragEnd = () => {
  draggingIndex.value = -1;
};

const handleDragOver = (event) => {
  if (!props.draggable) return;

  if (event.dataTransfer.types.includes('application/x-supesales-recipient')) {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    isDragOver.value = true;
  } else {
    event.dataTransfer.dropEffect = 'none';
    isDragOver.value = false;
  }
};

const handleDragEnter = (event) => {
  if (!props.draggable) return;
  if (event.dataTransfer.types.includes('application/x-supesales-recipient')) {
    isDragOver.value = true;
  }
};

const handleDragLeave = (event) => {
  if (!props.draggable) return;
  if (!event.currentTarget.contains(event.relatedTarget)) {
    isDragOver.value = false;
  }
};

const handleDrop = (event) => {
  if (!props.draggable) return;
  isDragOver.value = false;
  
  const dataString = event.dataTransfer.getData('application/x-supesales-recipient');
  
  if (dataString) {
    try {
      const data = JSON.parse(dataString);
      if (data.sourceField !== props.fieldId) {
        emit('item-dropped', {
          item: data.item,
          sourceField: data.sourceField,
          targetField: props.fieldId
        });
      }
    } catch (e) {
      console.error('Error parsing dropped data', e);
    }
  }
};
 

onMounted(() => {
  if (props.autofocus) {
    focusInput();
  }
  scrollToRight();
});
</script>

<style scoped>
.multi_select_input {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  padding: 5px;
  border-radius: 4px;
  position: relative;
  width: 100%;
  align-items: center;
  border: 1px solid transparent; /* prevent layout shift */
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.multi_select_input.drag-over {
  background-color: var(--white-opacity-12);
  border: 1px dashed var(--primary-color, #1890ff);
}

.scrollable-mode {
  flex-wrap: nowrap;
}

.content-container {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  width: 100%;
  align-items: center;
}

.scrollable-content {
  flex-wrap: nowrap;
  overflow-x: auto;
  white-space: nowrap;
  max-width: 100%;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.scrollable-content::-webkit-scrollbar {
  display: none;
}

.selected_item_tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.selected_item_tag.draggable-tag {
  cursor: grab;
}

.selected_item_tag.draggable-tag:active {
  cursor: grabbing;
}

.selected_item_tag.dragging {
  opacity: 0.5;
  transform: scale(0.95);
}

.scrollable-item {
  flex-shrink: 0;
  white-space: nowrap;
}

.input_field {
  border: none;
  outline: none;
  flex-grow: 1;
  background-color: transparent;
  color: var(--text-color);
  min-width: 100px;
  max-width: 100%;
}

.scrollable-input {
  flex-shrink: 0;
  min-width: 150px;
}

.remove_icon {
  width: 14px;
  height: 14px;
  cursor: pointer;
}

.dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  width: 450px;
  color: var(--text-color);
  background-color: var(--dropdown-bg);
  border: 1px solid var(--border-input-box);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 300px;
  overflow-y: scroll;
  padding: 4px;
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
  opacity: 0;
  animation: fade_in 0.3s forwards;
  scrollbar-width: thin;
  scrollbar-color: var(--white-opacity-12) transparent;
  -ms-overflow-style: none;
}

/* Webkit browsers (Chrome, Safari, newer versions of Edge) */
.dropdown::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.dropdown::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 4px;
}

.dropdown::-webkit-scrollbar-thumb {
  background-color: var(--white-opacity-12);
  border-radius: 4px;
  border: 1px solid transparent;
  background-clip: content-box;
}

.dropdown::-webkit-scrollbar-thumb:hover {
  background-color: var(--white-opacity-20);
}

/* For IE and Edge */
.dropdown::-ms-scrollbar {
  width: 8px;
  height: 8px;
}

.dropdown::-ms-scrollbar-track {
  background: transparent;
  border-radius: 4px;
}

.dropdown::-ms-scrollbar-thumb {
  background-color: var(--white-opacity-12);
  border-radius: 4px;
}

.dropdown::-ms-scrollbar-thumb:hover {
  background-color: var(--white-opacity-20);
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.dropdown-leave-active {
  animation: fade_out 0.3s forwards;
}

.dropdown_item {
  padding: 8px;
  cursor: pointer;
  color: var(--text-color);
  font-family: "Proxima Nova", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 17px;
}

.dropdown_item:hover {
  background-color: var(--white-opacity-12);
}

@keyframes fade_in {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fade_out {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-10px);
  }
}
</style>
